import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"

import { cn } from "../lib/utils"

const menuButtonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg px-4 h-11 min-h-9 text-body-md font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
  {
    variants: {
      variant: {
        default: "bg-background text-text hover:bg-background/80",
        "selected-primary": "bg-primary text-bg-white",
        "selected-secondary": "bg-secondary text-bg-white",
        "selected-neutral": "bg-gray text-bg-white",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface MenuButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof menuButtonVariants> {
  selected?: boolean
  role?: "user" | "staff" | "neutral"
}

const MenuButton = React.forwardRef<HTMLButtonElement, MenuButtonProps>(
  ({ className, variant, selected, role = "neutral", ...props }, ref) => {
    // Determine variant based on selected state and role
    let computedVariant = variant
    if (selected && !variant) {
      switch (role) {
        case "user":
          computedVariant = "selected-primary"
          break
        case "staff":
          computedVariant = "selected-secondary"
          break
        default:
          computedVariant = "selected-neutral"
      }
    } else if (!variant) {
      computedVariant = "default"
    }

    return (
      <button
        ref={ref}
        className={cn(menuButtonVariants({ variant: computedVariant, className }))}
        {...props}
      />
    )
  }
)
MenuButton.displayName = "MenuButton"

export { MenuButton, menuButtonVariants }
